/**
 * ArTableBundleRows
 *
 * Renders rows when persistent bundles exist.
 * Iterates ArBundleDisplay.items and delegates to:
 *   - ArBundleHeaderRow for bundle headers
 *   - ArBundleContinuationRow for a bundle whose header is on the previous page
 *   - ArRow (with bundleRole) for bundle members
 *   - ArRow (normal) for ungrouped rows
 *
 * A continuation item stands for no document and carries no `rowId`, so it is
 * branched before the ArRow fallthrough and keyed by its bundleId (SYD-2430). It
 * still takes a zebra stripe: `rowIndex` counts painted stripes, nothing else.
 * `disabled` reaches it as it reaches the header: one collapse key, two carets on
 * two pages, and a read-only table must not leave one live and the other dead. It
 * pins its message at `frozenLeftOffsetsByIndex[0]` — the very offset the header
 * pins its own label cell at, so parity holds by construction and an empty array
 * (nothing frozen) leaves both rows scrolling away together. It takes no PDF
 * guard where the header takes one: export drops the page-size limit, so every
 * header is in the window and no continuation item is ever minted.
 *
 * Subscribes to `bundles.collapsedBundles` and to nothing else on the UI state:
 * this component owns the element list for every row, so a read of the whole
 * proxy re-built that list on each cell focus, selection or pagination write.
 * `collapsed` is resolved through a named const so the per-bundle key is tracked
 * even in PDF mode, where the flag is forced off.
 * A header item's `rowId` is its own bundleId (persisted shape: `bundleRole` is
 * 'header' and `bundleId` is the row's docId), so it keys `bundleMap` directly —
 * that is where the header's honest member census comes from (SYD-2406).
 * `cardMode` passes through to the header, continuation and member rows.
 */

import { FC, memo, JSX, useContext, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import { usePdfExportMode } from '../usePdfExportMode';
import ArRow from './ArRow';
import ArBundleHeaderRow from './ArBundleHeaderRow';
import ArBundleContinuationRow from './ArBundleContinuationRow';
import { ArTableUIStateContext } from '../context';
import type { ArBundleDisplay } from '../bundleDisplay';
import type { ArColumnDefinition, TypeId } from '#types/System.Artifacts.types';

const NOOP = () => {};

type ArTableBundleRowsProps = {
  bundleResult: ArBundleDisplay;
  columns: ArColumnDefinition[];
  disabled: boolean;
  stickyFirstColumn: boolean;
  frozenLeftCount?: number;
  frozenLeftOffsetsByIndex?: number[];
  protectedTypeIds: ReadonlySet<TypeId>;
  formulaTypeIds: ReadonlySet<TypeId>;
  formulaSubFieldsByColumn: ReadonlyMap<TypeId, ReadonlySet<string>>;
  kitCalcDriverTypeIds: ReadonlySet<TypeId>;
  manualReorderEnabled?: boolean;
  cardMode?: boolean;
};

const ArTableBundleRows: FC<ArTableBundleRowsProps> = ({
  bundleResult,
  columns,
  disabled,
  stickyFirstColumn,
  frozenLeftCount = 0,
  frozenLeftOffsetsByIndex = [],
  protectedTypeIds,
  formulaTypeIds,
  formulaSubFieldsByColumn,
  kitCalcDriverTypeIds,
  manualReorderEnabled = false,
  cardMode = false,
}) => {
  const uiState = useContext(ArTableUIStateContext)!;
  const collapsedBundlesSnap = useSnapshot(uiState.bundles.collapsedBundles);
  const pdfExportMode = usePdfExportMode();

  const toggleBundleCollapsed = useCallback((bundleId: string) => {
    if (uiState.bundles.collapsedBundles[bundleId] === true) {
      delete uiState.bundles.collapsedBundles[bundleId];
    } else {
      uiState.bundles.collapsedBundles[bundleId] = true;
    }
  }, [uiState.bundles.collapsedBundles]);

  const elements: JSX.Element[] = [];
  let rowIndex = 0;

  for (const item of bundleResult.items) {
    if (item.kind === 'bundleHeader') {
      const bundleId = item.rowId;
      const collapsedInState = collapsedBundlesSnap[bundleId] === true;
      const collapsed = pdfExportMode ? false : collapsedInState;
      const bundle = bundleResult.bundleMap.get(bundleId);
      elements.push(
        <ArBundleHeaderRow
          key={`bundle:${item.rowId}`}
          rowId={item.rowId}
          bundleType={item.bundleType}
          memberRowIds={item.memberRowIds}
          memberCount={bundle?.memberCount ?? item.memberRowIds.length}
          withheldMemberCount={bundle?.withheldMemberCount ?? 0}
          collapsed={collapsed}
          onToggleCollapsed={pdfExportMode ? NOOP : toggleBundleCollapsed}
          columns={columns}
          disabled={disabled}
          stickyFirstColumn={stickyFirstColumn}
          frozenLeftCount={frozenLeftCount}
          frozenLeftOffsetsByIndex={frozenLeftOffsetsByIndex}
          manualReorderEnabled={manualReorderEnabled}
          protectedTypeIds={protectedTypeIds}
          formulaTypeIds={formulaTypeIds}
          formulaSubFieldsByColumn={formulaSubFieldsByColumn}
          kitCalcDriverTypeIds={kitCalcDriverTypeIds}
          continuesOnNextPage={item.continuesOnNextPage}
          cardMode={cardMode}
        />
      );
      rowIndex += 1;
      continue;
    }

    if (item.kind === 'bundleContinuation') {
      const collapsedInState = collapsedBundlesSnap[item.bundleId] === true;
      elements.push(
        <ArBundleContinuationRow
          key={`bundle-continuation:${item.bundleId}`}
          bundleId={item.bundleId}
          bundleType={item.bundleType}
          label={item.label}
          continuesOnNextPage={item.continuesOnNextPage}
          collapsed={collapsedInState}
          onToggleCollapsed={toggleBundleCollapsed}
          disabled={disabled}
          stickyFirstColumn={stickyFirstColumn}
          stickyLabelLeft={frozenLeftOffsetsByIndex[0]}
          cardMode={cardMode}
        />
      );
      rowIndex += 1;
      continue;
    }

    const isMember = item.kind === 'bundleMember';
    const bundleRole = isMember ? 'member' as const : undefined;

    elements.push(
      <ArRow
        key={item.rowId}
        rowId={item.rowId}
        rowIndex={rowIndex}
        disabled={disabled}
        stickyFirstColumn={stickyFirstColumn}
        frozenLeftCount={frozenLeftCount}
        frozenLeftOffsetsByIndex={frozenLeftOffsetsByIndex}
        manualReorderEnabled={manualReorderEnabled}
        columns={columns}
        protectedTypeIds={protectedTypeIds}
        formulaTypeIds={formulaTypeIds}
        formulaSubFieldsByColumn={formulaSubFieldsByColumn}
        bundleRole={bundleRole}
        bundleId={isMember ? item.bundleId : undefined}
        bundleType={isMember ? item.bundleType : undefined}
        cardMode={cardMode}
      />
    );
    rowIndex += 1;
  }

  return <>{elements}</>;
};

export default memo(ArTableBundleRows);
